import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Loader2, ArrowLeft, XCircle } from "lucide-react";
import { http, API } from "@/lib/api";

function useStatus(sid) {
  const [state, setState] = useState({ phase: sid ? "checking" : "error", data: null });
  useEffect(() => {
    if (!sid) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await http.get(`/payments/status/${sid}`);
        if (data?.payment_status === "paid") return setState({ phase: "paid", data });
        if (["expired", "failed"].includes(data?.payment_status)) return setState({ phase: "error", data });
      } catch (e) { /* retry */ }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setState({ phase: "error", data: null });
      return undefined;
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);
  return state;
}

function Success() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const { phase, data } = useStatus(sid);
  if (phase === "checking") return <p className="flex items-center gap-3 text-[var(--stone)]" data-testid="payment-checking"><Loader2 className="animate-spin" size={18} /> Verifying your payment…</p>;
  if (phase === "error") return <p className="text-[var(--stone)]" data-testid="payment-error">We can't confirm the payment right now. If you were charged, please contact us.</p>;
  return (
    <div data-testid="payment-success">
      {data?.track?.cover && <img src={data.track.cover} alt="" className="mb-8 h-40 w-40 object-cover" />}
      <p className="eyebrow">Payment confirmed</p>
      <h1 className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] md:text-7xl">{data?.track?.title ?? "Your track"}</h1>
      <p className="mt-4 text-[var(--stone)]">Thanks for the support. Download the file below and keep this link.</p>
      <a data-testid="checkout-download-track-btn" href={`${API}/download/${sid}`} className="mt-8 inline-flex items-center gap-3 rounded-full bg-[var(--gold)] px-7 py-4 font-mono text-sm uppercase tracking-[0.2em] text-[var(--ink)] transition-transform hover:scale-[1.03]">
        <Download size={16} /> Download track
      </a>
    </div>
  );
}

export default function PaymentResult({ success = false }) {
  return (
    <div className="flex min-h-screen items-center px-5 md:px-16">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="max-w-2xl">
        <Link to="/" data-testid="payment-back-home" className="mb-12 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[var(--stone)] hover:text-[var(--gold)]"><ArrowLeft size={14} /> Back to site</Link>
        {success ? <Success /> : (
          <div data-testid="payment-cancelled">
            <XCircle className="text-[var(--gold)]" size={36} />
            <h1 className="mt-6 font-display text-5xl font-black uppercase md:text-7xl">Payment cancelled</h1>
            <p className="mt-4 text-[var(--stone)]">No charge was made. You can try again whenever you want.</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
